import { pdf, Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import logo from '../assets/images/logo.webp';
import logoFallback from '../assets/images/logo-placeholder.jpg';

const styles = StyleSheet.create({
	page: {
		padding: 24,
		fontSize: 10,
		fontFamily: 'Helvetica',
		flexDirection: 'column',
	},
	content: {
		width: '100%',
		flexDirection: 'column',
		alignItems: 'stretch',
	},
	// Logo + meta on one row: fixed-width left column so logo shares x-origin with title/table (no space-between gap)
	headerRow: {
		flexDirection: 'row',
		width: '100%',
		marginBottom: 12,
		alignItems: 'flex-start',
	},
	logoColumn: {
		width: 110,
		minWidth: 110,
		maxWidth: 110,
		marginLeft: 0,
		paddingLeft: 0,
	},
	headerLogo: {
		width: 110,
		height: 34,
		objectFit: 'contain',
		// Keep artwork flush left inside the box (contain() otherwise centers wide assets)
		objectPosition: 'left',
		margin: 0,
		padding: 0,
	},
	metaContainer: {
		flex: 1,
		marginLeft: 8,
		alignItems: 'flex-end',
	},
	metaText: {
		fontSize: 9,
		marginBottom: 2,
	},
	title: {
		fontSize: 14,
		fontWeight: 700,
		marginBottom: 10,
		marginTop: 0,
		marginLeft: 0,
		paddingLeft: 0,
		width: '100%',
		textAlign: 'left',
	},
	table: {
		width: '100%',
		alignSelf: 'stretch',
		marginLeft: 0,
		paddingLeft: 0,
		borderWidth: 1,
		borderColor: '#D9D9D9',
	},
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 1,
		borderBottomColor: '#E8E8E8',
	},
	tableHeaderCell: {
		padding: 6,
		fontSize: 9,
		fontWeight: 700,
		backgroundColor: '#F7F7F7',
	},
	tableCell: {
		padding: 6,
		fontSize: 9,
	},
	footer: {
		marginTop: 10,
		marginLeft: 0,
		paddingLeft: 0,
		fontSize: 8,
		color: '#666',
	},
});

const toDataUrl = (imageSrc, outputType = 'image/png') =>
	new Promise((resolve, reject) => {
		const image = new window.Image();
		// Required for remote URLs so canvas is not tainted and toDataURL works when CORS allows it
		if (typeof imageSrc === 'string' && /^https?:\/\//i.test(imageSrc)) {
			image.crossOrigin = 'anonymous';
		}
		image.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = image.naturalWidth || image.width;
			canvas.height = image.naturalHeight || image.height;
			const context = canvas.getContext('2d');
			if (!context) {
				reject(new Error('Failed to create canvas context'));
				return;
			}
			context.drawImage(image, 0, 0);
			try {
				resolve(canvas.toDataURL(outputType));
			} catch {
				reject(new Error('Failed to export logo to data URL'));
			}
		};
		image.onerror = () => reject(new Error('Failed to load logo image'));
		image.src = imageSrc;
	});

const resolveBundledLogoDataUrl = async () => {
	try {
		return await toDataUrl(logo);
	} catch {
		return logoFallback;
	}
};

/**
 * react-pdf Image often fails to render raw remote URLs in the PDF output.
 * Normalize to a data URL when possible; always fall back to Funding Souq branding.
 */
const resolvePdfLogoSource = async (dynamicLogoSrc) => {
	const trimmed = typeof dynamicLogoSrc === 'string' ? dynamicLogoSrc.trim() : '';
	if (trimmed) {
		try {
			return await toDataUrl(trimmed);
		} catch {
			// Remote logo blocked by CORS or invalid — use platform logo
		}
	}
	return resolveBundledLogoDataUrl();
};

const PdfTableDocument = ({ logoSrc, title, investorDate, clientName, headers, rows }) => {
	const columnCount = headers.length || 1;
	const cellWidth = `${100 / columnCount}%`;

	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<View style={styles.content}>
					<View style={styles.headerRow}>
						<View style={styles.logoColumn}>
							<Image src={logoSrc} style={styles.headerLogo} />
						</View>
						<View style={styles.metaContainer}>
							<Text style={styles.metaText}>Investor Date: {investorDate || '-'}</Text>
							<Text style={styles.metaText}>Client Name: {clientName || '-'}</Text>
						</View>
					</View>

					<Text style={styles.title}>{title}</Text>

					<View style={styles.table}>
						<View style={styles.tableRow}>
							{headers.map((header) => (
								<Text key={header} style={{ ...styles.tableHeaderCell, width: cellWidth }}>
									{header}
								</Text>
							))}
						</View>
						{rows.map((row, index) => (
							<View key={`row-${index}`} style={styles.tableRow}>
								{row.map((cell, cellIndex) => (
									<Text
										key={`cell-${index}-${cellIndex}`}
										style={{ ...styles.tableCell, width: cellWidth }}>
										{cell ?? '-'}
									</Text>
								))}
							</View>
						))}
					</View>

					<Text style={styles.footer}>Generated by Funding Souq</Text>
				</View>
			</Page>
		</Document>
	);
};

export const downloadTablePdf = async ({
	title,
	investorDate,
	clientName,
	logoSrc: dynamicLogoSrc,
	headers = [],
	rows = [],
	fileName = 'document.pdf',
}) => {
	const logoSrc = await resolvePdfLogoSource(dynamicLogoSrc);
	const blob = await pdf(
		<PdfTableDocument
			logoSrc={logoSrc}
			title={title}
			investorDate={investorDate}
			clientName={clientName}
			headers={headers}
			rows={rows}
		/>
	).toBlob();

	saveAs(blob, fileName);
};
